import { useCallback, useEffect, useState } from "react";
import { useOrganization, useUser } from "@clerk/react";
import { usePaginatedQuery, useQuery } from "convex/react";
import { PageHeader } from "../PageHeader";
import { FilterChips } from "../shared/FilterChips";
import { SectionBoundary } from "../shared/SectionBoundary";
import { FallbackBoundary } from "../shared/FallbackBoundary";
import { usePeople } from "../aiUsage/usePeople";
import { useNow } from "../aiUsage/useNow";
import {
  formatCount,
  formatDuration,
  formatWhen,
  notRoutedWhy,
  periodStart,
  projectLabel,
  sessionStatus,
  sourceLabel,
  type SessionStatus,
} from "../aiUsage/format";
import {
  nextSessionChange,
  sessionCost,
  sessionErrors,
  sessionFlags,
  type SessionView,
} from "../aiUsage/sessions";
import { api as convexApi } from "../../../lib/convexApi";

type Period = "1" | "7" | "30";
// listSessions takes one filter at a time (convex/observability/queries.ts
// sessionIndex: each is one index read), so the controls replace each other
// rather than combine.
type Filter =
  | { kind: "flagged" }
  | { kind: "tool"; tool: string }
  | { kind: "person"; userId: string }
  | null;

// The list's period and filter. The Security Room keeps it while a session
// is open, so Back returns to the same view (the loaded pages start over).
export interface SessionsView {
  period: Period;
  filter: Filter;
  /** The account the view was chosen in (useAccount); the default has none. */
  owner?: string;
}
export const DEFAULT_SESSIONS_VIEW: SessionsView = { period: "7", filter: null };

// The viewer's role, from billing.queries.myRole: "loading" until it answers,
// "unknown" when it failed, so the page shows what any viewer may see.
type Role = "admin" | "member" | "loading" | "unknown";

// convex/observability/gateway.ts `tools`, as merged (PR 2). Members get the
// same list, with null times and setters, which the filter does not read.
interface ToolList {
  tools: Array<{ tool: string; toolName: string }>;
  truncated: boolean;
}

const PAGE = 50;
const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
const STATUS_PILL: Record<SessionStatus, string> = {
  live: " is-ok",
  idle: "",
  ended: "",
  stopped: " is-crit",
};

// The start of the period the list shows. 7 and 30 days are whole UTC days,
// as the AI overview counts them (periodStart), so the two pages cover the
// same sessions. 24 hours starts at the hour 24 hours ago. The value holds
// until the period's next boundary (see SessionsBody), so the subscription
// keeps its arguments in between.
function sinceFor(period: Period, now: number): number {
  if (period === "1") return Math.floor((now - DAY_MS) / HOUR_MS) * HOUR_MS;
  return periodStart(Number(period), now);
}

export function AiSessionsPage({
  onOpen,
  view,
  onViewChange,
}: {
  onOpen: (id: string) => void;
  /** The period and filter, when the caller keeps them; else the page does. */
  view?: SessionsView;
  onViewChange?: (view: SessionsView) => void;
}) {
  const [ownView, setOwnView] = useState<SessionsView>(DEFAULT_SESSIONS_VIEW);
  // A view is kept for the account it was chosen in: another org, or another
  // user, starts from the default, so a person chosen in one org is never
  // looked for in another.
  const owner = useAccount();
  const kept = view ?? ownView;
  const current = kept.owner === owner ? kept : DEFAULT_SESSIONS_VIEW;
  const setView = useCallback(
    (next: SessionsView) => (onViewChange ?? setOwnView)({ ...next, owner }),
    [onViewChange, owner]
  );
  const [role, setRole] = useState<Role>("loading");
  return (
    <>
      <PageHeader title="AI sessions" subtitle="Steerly terminals and chats" />
      <div className="sr-page-body">
        {/* Only the role read sits in this boundary. The role decides just the
            person controls and the scope note, so a failed read leaves the
            role unknown, and an error in the list itself is the list's own
            (the SectionBoundary below), never taken for a role it lacks. */}
        {/* A failed read is not for good: Retry reads it again, and so does
            another account (the boundary is keyed by it). */}
        <FallbackBoundary
          key={owner}
          label="AI sessions role"
          fallback={(retry) => <RoleUnread onRole={setRole} onRetry={retry} />}
        >
          <RoleProbe onRole={setRole} />
        </FallbackBoundary>
        <SectionBoundary label="AI sessions">
          <SessionsBody
            owner={owner}
            role={role}
            view={current}
            setView={setView}
            onOpen={onOpen}
          />
        </SectionBoundary>
      </div>
    </>
  );
}

// Reads the viewer's role and reports it up; renders nothing.
function RoleProbe({ onRole }: { onRole: (role: Role) => void }) {
  const myRole = useQuery((convexApi as any).billing.queries.myRole, {}) as
    | { isAdmin?: boolean }
    | undefined;
  const role: Role = myRole === undefined ? "loading" : myRole.isAdmin ? "admin" : "member";
  return <ReportRole role={role} onRole={onRole} />;
}

function ReportRole({ role, onRole }: { role: Role; onRole: (role: Role) => void }) {
  useEffect(() => onRole(role), [role, onRole]);
  return null;
}

// A role read that failed: the page shows what any viewer may see, and says
// why, with a Retry that reads the role again.
function RoleUnread({ onRole, onRetry }: { onRole: (role: Role) => void; onRetry: () => void }) {
  useEffect(() => onRole("unknown"), [onRole]);
  return (
    <div className="sr-ai-scope">
      Your role could not be read.{" "}
      <button
        type="button"
        className="sr-btn sr-btn--ghost"
        aria-label="Retry reading your role"
        onClick={onRetry}
      >
        Retry
      </button>
    </div>
  );
}

// The signed-in account a kept view belongs to: its org (none for a personal
// workspace) and its user.
function useAccount(): string {
  const { organization } = useOrganization();
  const { user } = useUser();
  return JSON.stringify([organization?.id ?? null, user?.id ?? null]);
}

function SessionsBody({
  owner,
  role,
  view,
  setView,
  onOpen,
}: {
  /** The signed-in account (useAccount): another account is another read. */
  owner: string;
  role: Role;
  view: SessionsView;
  setView: (view: SessionsView) => void;
  onOpen: (id: string) => void;
}) {
  const { period } = view;
  // A person filter applies only while the role reads admin: listSessions
  // refuses a member one for someone else, and only an admin has the Person
  // control that shows and clears it. An admin demoted while the page is open
  // reads as a member here, so the filter goes at once from the list's reads
  // and from the view kept for Back (as in the redesign's list). While the
  // role loads, the list waits for it (below); while the role cannot be read,
  // the filter is set aside, kept for a role read that says admin.
  const personFilter = view.filter?.kind === "person";
  const demoted = role === "member" && personFilter;
  useEffect(() => {
    if (demoted) setView({ period, filter: null });
  }, [demoted, period, setView]);
  const filter = personFilter && role !== "admin" ? null : view.filter;
  // A new period keeps the view's own filter, so a person filter set aside
  // while the role loads or cannot be read stays kept (a member's is dropped
  // by the effect above).
  const setPeriod = (p: Period) => setView({ period: p, filter: view.filter });
  const setFilter = (f: Filter) => setView({ period, filter: f });
  // The clock the period is read against (useNow, as the AI overview's): read
  // again at the period's next boundary (UTC midnight for days, the hour for
  // 24 hours), and after sleep, so a page left open moves its period on
  // instead of covering more than it says. It is also read when the period
  // changes: a reading kept for a 7 days view can be hours old, too old for
  // 24 hours. A new start is a new query, so the loaded pages start over.
  const step = period === "1" ? HOUR_MS : DAY_MS;
  const ticked = useNow((n) => (Math.floor(n / step) + 1) * step);
  const [picked, setPicked] = useState(() => ({ period, at: Date.now() }));
  if (picked.period !== period) setPicked({ period, at: Date.now() });
  const now = Math.max(ticked, picked.at);
  const since = sinceFor(period, now);
  const { nameFor, people, capped } = usePeople();
  const isAdmin = role === "admin";
  const args = filter ? { since, filter } : { since };
  // The tool and person filters are selects, not chips: a tool key can read
  // "all" or "flagged", and gateway.tools lists up to 200 tools.
  const show = filter === null ? "all" : filter.kind === "flagged" ? "flagged" : "";
  // The chosen person always has an option, named as the list names them,
  // when the member list does not hold them (removed from the org since, or
  // past its first 1,000), so the select never reads "Everyone" while the
  // sessions are filtered: ToolSelect does the same for a tool.
  const person = filter?.kind === "person" ? filter.userId : "";
  const personListed = person === "" || people.some((p) => p.userId === person);

  return (
    <>
      {role === "member" && <div className="sr-ai-scope">Showing your sessions</div>}
      <div className="sr-ai-toolbar">
        <FilterChips
          label="Period"
          value={period}
          onChange={setPeriod}
          options={[
            { value: "1", label: "24 hours" },
            { value: "7", label: "7 days" },
            { value: "30", label: "30 days" },
          ]}
        />
        <FilterChips
          label="Show"
          value={show}
          onChange={(v) => setFilter(v === "flagged" ? { kind: "flagged" } : null)}
          options={[
            { value: "all", label: "All" },
            { value: "flagged", label: "Flagged" },
          ]}
        />
        {/* A tool list that fails to load leaves only "Any tool". */}
        <FallbackBoundary
          label="AI sessions tools"
          fallback={(retry) => (
            <ToolSelect tools={[]} filter={filter} setFilter={setFilter} onRetry={retry} />
          )}
        >
          <ToolSelectWithTools filter={filter} setFilter={setFilter} />
        </FallbackBoundary>
        {isAdmin && (
          <div className="sr-filter">
            <span className="sr-filter__label">Person:</span>
            <select
              aria-label="Person"
              className="sr-select"
              value={person}
              onChange={(e) =>
                setFilter(e.target.value ? { kind: "person", userId: e.target.value } : null)
              }
            >
              <option value="">Everyone</option>
              {people.map((p) => (
                <option key={p.userId} value={p.userId}>
                  {p.name}
                </option>
              ))}
              {!personListed && <option value={person}>{nameFor(person)}</option>}
            </select>
            {/* usePeople reads at most 1,000 members. */}
            {capped && <span className="sr-ai-note">Lists the first 1,000 members</span>}
          </div>
        )}
      </div>

      {/* Its own boundary, keyed by the account and the arguments (as the
          redesign's list): a list that fails keeps the filters above, and
          another account, period or filter tries again. A person filter
          waits for the role, so a member is never asked one. */}
      {role === "loading" && personFilter ? (
        <div className="sr-empty">Loading sessions…</div>
      ) : (
        <SectionBoundary key={JSON.stringify([owner, args])} label="AI sessions">
          <SessionsList
            args={args}
            filtered={filter !== null}
            role={role}
            nameFor={nameFor}
            onOpen={onOpen}
          />
        </SectionBoundary>
      )}
    </>
  );
}

function ToolSelectWithTools({
  filter,
  setFilter,
}: {
  filter: Filter;
  setFilter: (f: Filter) => void;
}) {
  const tools = useQuery((convexApi as any).observability.gateway.tools, {}) as
    | ToolList
    | undefined;
  return <ToolSelect tools={tools?.tools ?? []} filter={filter} setFilter={setFilter} />;
}

// The tool filter. The chosen tool always has an option, by its key when the
// list does not hold it (the list failed, is still loading, or stops at 200),
// so the select never reads "Any tool" while the sessions are filtered.
// `onRetry`: the list failed to load; it is said beside the select, with a
// Retry that reads it again.
function ToolSelect({
  tools,
  filter,
  setFilter,
  onRetry,
}: {
  tools: ToolList["tools"];
  filter: Filter;
  setFilter: (f: Filter) => void;
  onRetry?: () => void;
}) {
  const chosen = filter?.kind === "tool" ? filter.tool : "";
  const listed = chosen === "" || tools.some((t) => t.tool === chosen);
  return (
    <div className="sr-filter">
      <span className="sr-filter__label">Tool:</span>
      <select
        aria-label="Tool"
        className="sr-select"
        value={chosen}
        onChange={(e) => setFilter(e.target.value ? { kind: "tool", tool: e.target.value } : null)}
      >
        <option value="">Any tool</option>
        {tools.map((t) => (
          <option key={t.tool} value={t.tool}>
            {t.toolName}
          </option>
        ))}
        {!listed && <option value={chosen}>{chosen}</option>}
      </select>
      {onRetry && (
        <>
          <span className="sr-ai-note">Tools could not load.</span>
          <button
            type="button"
            className="sr-btn sr-btn--ghost"
            aria-label="Retry the tool list"
            onClick={onRetry}
          >
            Retry
          </button>
        </>
      )}
    </div>
  );
}

function SessionsList({
  args,
  filtered,
  role,
  nameFor,
  onOpen,
}: {
  args: { since: number; filter?: Filter };
  filtered: boolean;
  role: Role;
  nameFor: (userId: string) => string;
  onOpen: (id: string) => void;
}) {
  const { results, status, loadMore } = usePaginatedQuery(
    (convexApi as any).observability.queries.listSessions,
    args,
    { initialNumItems: PAGE }
  );
  const sessions = results as SessionView[];
  // The time the statuses and start times are read against, read again when
  // a session shown would turn idle on its own and at local midnight (useNow).
  const now = useNow((n) => nextSessionChange(sessions, n));
  const isAdmin = role === "admin";

  // The table waits for the role as well as the first page, so the Person
  // column never pops in after the rows are drawn.
  if (status === "LoadingFirstPage" || role === "loading") {
    return <div className="sr-empty">Loading sessions…</div>;
  }
  if (sessions.length === 0) {
    return (
      <div className="sr-empty">
        {/* The AI overview's wording: the host records a session for a
            supported AI agent started in a Steerly terminal or chat, routed or
            not, and model calls only for a routed one. With the role unknown,
            the words fit either role. */}
        {filtered
          ? "No sessions match this filter in this period."
          : role === "member"
            ? "No AI sessions of yours in this period. Steerly records a session when you start Claude Code, Codex or another supported AI agent in a Steerly terminal or chat, and its model calls when the session goes through the AI gateway."
            : role === "admin"
              ? "No AI sessions in this period. Steerly records a session when someone starts Claude Code, Codex or another supported AI agent in a Steerly terminal or chat, and its model calls when the session goes through the AI gateway."
              : "No AI sessions to show in this period. Steerly records a session when Claude Code, Codex or another supported AI agent starts in a Steerly terminal or chat, and its model calls when the session goes through the AI gateway."}
      </div>
    );
  }

  return (
    <>
      <table className="sr-table">
        <thead>
          <tr>
            {isAdmin && <th>Person</th>}
            <th>Tool</th>
            <th>Project</th>
            <th>Source</th>
            <th>Started</th>
            <th className="is-num">Duration</th>
            <th className="is-num">Calls</th>
            <th className="is-num">Cost</th>
            <th>Flags</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => {
            const state = sessionStatus(s, now);
            // Every reason the session is flagged (sessionFlags); errors show
            // with the calls.
            const flags = sessionFlags(s);
            return (
              <tr key={s.id} className="is-clickable" onClick={() => onOpen(s.id)}>
                {isAdmin && <td>{nameFor(s.userId)}</td>}
                <td>
                  <button
                    type="button"
                    className="sr-link"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(s.id);
                    }}
                  >
                    {s.toolName}
                  </button>
                </td>
                <td>{projectLabel(s)}</td>
                <td>
                  {sourceLabel(s.sourceKind)},{" "}
                  {s.routed ? "routed" : notRoutedWhy("not routed", s.notRoutedReason)}
                </td>
                <td>{formatWhen(s.firstSeenAt, now)}</td>
                <td className="is-num">
                  {formatDuration((s.endedAt ?? s.lastSeenAt) - s.firstSeenAt)}
                </td>
                <td className="is-num">
                  {formatCount(s.calls)}
                  {sessionErrors(s)}
                </td>
                <td className="is-num">{s.calls === 0 ? "" : sessionCost(s)}</td>
                <td>{flags.join(", ")}</td>
                <td>
                  <span className={`sr-pill${STATUS_PILL[state]}`}>{state}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {status === "CanLoadMore" && (
        <button
          type="button"
          className="sr-btn sr-btn--ghost"
          style={{ marginTop: 12 }}
          onClick={() => loadMore(PAGE)}
        >
          Load more
        </button>
      )}
    </>
  );
}
